interface CustomElement extends HTMLElement {
  connectedCallback?(): void
  disconnectedCallback?(): void
}

type Constructor<T = CustomElement> = new (...args: any[]) => T

export type GestureEventPinch = CustomEvent<{ scale: number; centerX: number; centerY: number }>
export type GestureEventDrag = CustomEvent<{
  deltaX: number
  deltaY: number
  clientX: number
  clientY: number
  end: boolean
}>
export type GestureEventDoubleTap = CustomEvent<{ x: number; y: number }>

export function gesture<TBase extends Constructor>(Base: TBase) {
  return class extends Base {
    __pointers = new Map<number, { x: number; y: number }>()
    __lastTapTime = 0
    __lastPinchDistance = 0
    __dragStart: { x: number; y: number } | null = null

    connectedCallback(): void {
      if (super.connectedCallback) {
        super.connectedCallback()
      }
      this.addEventListener('pointerdown', this.handlePointerDown as EventListener)
    }

    disconnectedCallback(): void {
      if (super.disconnectedCallback) {
        super.disconnectedCallback()
      }
      this.removeEventListener('pointerdown', this.handlePointerDown as EventListener)
      this.removeGlobalListeners()
    }

    handlePointerDown = (e: PointerEvent) => {
      e.preventDefault()
      const point = { x: e.clientX, y: e.clientY }
      this.__pointers.set(e.pointerId, point)

      this.addGlobalListeners()
    }

    handlePointerMove = (e: PointerEvent) => {
      e.preventDefault()
      const point = { x: e.clientX, y: e.clientY }
      this.__pointers.set(e.pointerId, point)

      if (this.__pointers.size === 2) {
        this.handlePinch()
      } else if (this.__pointers.size === 1) {
        this.handleDrag(point, false)
      }
    }

    handlePointerUp = (e: PointerEvent) => {
      const point = { x: e.clientX, y: e.clientY }
      this.__pointers.delete(e.pointerId)

      if (this.__dragStart) {
        this.handleDrag(point, true)
      }

      if (this.__pointers.size === 0) {
        this.removeGlobalListeners()
        this.__dragStart = null
        this.__lastPinchDistance = 0
        this.handleTap(point)
      }
    }

    handlePinch() {
      const pointers = Array.from(this.__pointers.values()) as { x: number; y: number }[]
      const currentDistance = this.getDistance(pointers[0], pointers[1])

      if (this.__lastPinchDistance) {
        const scale = currentDistance / this.__lastPinchDistance
        const center = {
          x: (pointers[0].x + pointers[1].x) / 2,
          y: (pointers[0].y + pointers[1].y) / 2
        }

        this.dispatchEvent(
          new CustomEvent('pinch', {
            detail: { scale, centerX: center.x, centerY: center.y }
          })
        )
      }

      this.__lastPinchDistance = currentDistance
    }

    handleDrag(point: { x: number; y: number }, end: boolean) {
      if (this.__dragStart) {
        const deltaX = point.x - this.__dragStart.x
        const deltaY = point.y - this.__dragStart.y

        this.dispatchEvent(
          new CustomEvent('drag', {
            detail: { deltaX, deltaY, clientX: point.x, clientY: point.y, end }
          })
        )

        if (end) {
          this.__dragStart = null
        } else {
          this.__dragStart = point
        }
      } else if (!end && this.__pointers.size === 1) {
        /* dragging을 시작하려면, pointer 개수가 1개인 것을 (지연)확인해야한다. */
        setTimeout(() => {
          if (this.__pointers.size === 1 && !this.__dragStart) {
            this.__dragStart = point
          }
        }, 30)
      }
    }

    handleTap(point: { x: number; y: number }) {
      const currentTime = performance.now()
      const timeSinceLastTap = currentTime - this.__lastTapTime

      if (timeSinceLastTap < 300) {
        // 300ms is a common double-tap threshold
        this.dispatchEvent(
          new CustomEvent('doubletap', {
            detail: { x: point.x, y: point.y }
          })
        )
      }

      this.__lastTapTime = currentTime
    }

    getDistance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
      const dx = p1.x - p2.x
      const dy = p1.y - p2.y
      return Math.sqrt(dx * dx + dy * dy)
    }

    addGlobalListeners() {
      document.addEventListener('pointermove', this.handlePointerMove as EventListener)
      document.addEventListener('pointerup', this.handlePointerUp as EventListener)
      document.addEventListener('pointercancel', this.handlePointerUp as EventListener)
    }

    removeGlobalListeners() {
      document.removeEventListener('pointermove', this.handlePointerMove as EventListener)
      document.removeEventListener('pointerup', this.handlePointerUp as EventListener)
      document.removeEventListener('pointercancel', this.handlePointerUp as EventListener)
    }
  }
}
